/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { BookOpen, FileDown, Loader2, Info, TrendingUp, PiggyBank } from "lucide-react";
import { toast } from "sonner";
import { formatDateLong } from "@/lib/date";

function fmtEur(v) {
  return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function AdminLivreRecettes() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [margins, setMargins] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminApi.livreRecettes(year).catch((e) => { toast.error(formatApiError(e)); return null; }),
      adminApi.adminOnlyMargins(year).catch(() => null),
    ]).then(([lr, mg]) => { setData(lr); setMargins(mg); })
      .finally(() => setLoading(false));
  }, [year]);

  const groupedByMonth = useMemo(() => {
    if (!data?.entries) return [];
    const groups = {};
    for (const e of data.entries) {
      const dt = new Date(e.date_encaissement);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      (groups[key] ||= []).push(e);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("ma_token");
      const url = adminApi.livreRecettesPdfUrl(year);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `livre-recettes-${year}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (e) { toast.error("Erreur téléchargement PDF"); }
  };

  return (
    <div data-testid="admin-livre-recettes">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Comptabilité obligatoire</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Livre de <span className="italic gold-text">recettes.</span></h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Registre chronologique de toutes les recettes encaissées — conforme à l'article&nbsp;50-0 du CGI.
            À conserver 10 ans, présentable en cas de contrôle fiscal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input-luxe w-auto" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="lr-year">
            {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map((y) => <option key={y}>{y}</option>)}
          </select>
          <button onClick={downloadPdf} data-testid="lr-pdf" className="btn-gold inline-flex items-center gap-2">
            <FileDown size={14} /> Télécharger PDF
          </button>
        </div>
      </header>

      <div className="card-luxe p-4 mb-6 flex items-start gap-3 border-[#DCB457]/30" data-testid="lr-legal-note">
        <Info size={16} className="text-[#DCB457] mt-0.5 shrink-0" />
        <p className="text-[13px] text-white/70 leading-relaxed">
          Ce registre est <b>alimenté automatiquement</b> à partir de tes factures et factures d'acompte marquées "payée". La date de référence est la date d'encaissement (<code className="text-[#DCB457]">paid_at</code>). Un contrôleur fiscal peut te demander la version PDF signée — utilise le bouton ci-dessus pour la générer.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/55"><Loader2 className="animate-spin" /> Chargement…</div>
      ) : !data ? null : (
        <>
          {/* Summary card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="card-luxe p-5 border-[#DCB457]/45">
              <p className="eyebrow">Nombre d'entrées</p>
              <p className="font-serif-luxe text-3xl gold-text mt-2">{data.count}</p>
              <p className="text-[11px] text-white/45 mt-1">Recettes encaissées en {year}</p>
            </div>
            <div className="card-luxe p-5">
              <p className="eyebrow">Total encaissé</p>
              <p className="font-serif-luxe text-3xl gold-text mt-2">{fmtEur(data.total_ttc)}</p>
              <p className="text-[11px] text-white/45 mt-1">Base de déclaration URSSAF/impôts</p>
            </div>
            <div className="card-luxe p-5">
              <p className="eyebrow">Format</p>
              <p className="font-serif-luxe text-lg mt-2 flex items-center gap-2"><BookOpen size={16} className="text-[#DCB457]" /> Art. 50-0 du CGI</p>
              <p className="text-[11px] text-white/45 mt-1">Chronologique, sans blanc, sans rature</p>
            </div>
          </div>

          {/* Marges nettes sur forfaits admin-only */}
          {margins && margins.count > 0 && (
            <section className="mb-8" data-testid="lr-margins-section">
              <div className="mb-4 flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <p className="eyebrow text-purple-300">Pilotage</p>
                  <h2 className="font-serif-luxe text-2xl md:text-3xl mt-1">
                    Marges nettes sur <span className="italic text-purple-300">mises en relation.</span>
                  </h2>
                  <p className="text-white/55 text-[13px] mt-2 max-w-2xl">
                    Marges nettes réelles réalisées sur les forfaits admin-only, calculées après cotisations URSSAF et coût prestataire.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="card-luxe p-5 border-purple-400/40 bg-purple-500/[0.03]">
                  <p className="eyebrow text-purple-300">CA admin-only</p>
                  <p className="font-serif-luxe text-3xl text-purple-200 mt-2" data-testid="lr-margins-ca">{fmtEur(margins.total_ca_admin_only)}</p>
                  <p className="text-[11px] text-white/45 mt-1">{margins.count} ligne{margins.count > 1 ? "s" : ""} facturée{margins.count > 1 ? "s" : ""}</p>
                </div>
                <div className="card-luxe p-5 border-emerald-400/40 bg-emerald-500/[0.03]">
                  <p className="eyebrow text-emerald-300">Marge nette totale</p>
                  <p className="font-serif-luxe text-3xl text-emerald-200 mt-2 flex items-center gap-2" data-testid="lr-margins-total">
                    <TrendingUp size={22} strokeWidth={1.3} /> {fmtEur(margins.total_margin_net)}
                  </p>
                  <p className="text-[11px] text-white/45 mt-1">Après URSSAF & coût prestataire</p>
                </div>
                <div className="card-luxe p-5">
                  <p className="eyebrow flex items-center gap-2"><PiggyBank size={13} strokeWidth={1.5} className="text-[#DCB457]" /> Ratio net / CA</p>
                  <p className="font-serif-luxe text-3xl gold-text mt-2">
                    {margins.total_ca_admin_only > 0 ? ((margins.total_margin_net / margins.total_ca_admin_only) * 100).toFixed(1) : "0"} %
                  </p>
                  <p className="text-[11px] text-white/45 mt-1">Rentabilité de vos re-facturations</p>
                </div>
              </div>
              <div className="card-luxe overflow-hidden" data-testid="lr-margins-table">
                <table className="w-full text-sm">
                  <thead className="bg-black/60 text-[10px] uppercase tracking-[0.2em] text-purple-300">
                    <tr>
                      <th className="px-3 py-3 text-left">Date</th>
                      <th className="px-3 py-3 text-left">Facture</th>
                      <th className="px-3 py-3 text-left">Client</th>
                      <th className="px-3 py-3 text-left">Forfait</th>
                      <th className="px-3 py-3 text-right">CA TTC</th>
                      <th className="px-3 py-3 text-right">− URSSAF</th>
                      <th className="px-3 py-3 text-right">− Prestataire</th>
                      <th className="px-3 py-3 text-right">Marge nette</th>
                    </tr>
                  </thead>
                  <tbody>
                    {margins.entries.map((e, i) => (
                      <tr key={i} className="border-t border-purple-400/10 hover:bg-purple-500/5" data-testid={`lr-margin-row-${i}`}>
                        <td className="px-3 py-2.5 text-white/70 text-[12px]">{formatDateLong(e.date)}</td>
                        <td className="px-3 py-2.5 font-mono text-[#DCB457] text-[11px]">{e.invoice_number || "—"}</td>
                        <td className="px-3 py-2.5 text-white/80">{e.client_name}</td>
                        <td className="px-3 py-2.5 text-white/70 text-[12px]">{e.package_name}{e.qty > 1 && <span className="text-white/45"> × {e.qty}</span>}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{fmtEur(e.ca_ttc)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-red-300 text-[12px]">− {fmtEur(e.urssaf_due)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-red-300 text-[12px]">− {fmtEur(e.prestataire_total)}</td>
                        <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-300">{fmtEur(e.margin_net)}</td>
                      </tr>
                    ))}
                    <tr className="bg-black border-t-2 border-emerald-400/50">
                      <td colSpan={4} className="px-3 py-3.5 text-right font-serif-luxe text-base">MARGE NETTE {year}</td>
                      <td className="px-3 py-3.5 text-right font-mono text-white/70">{fmtEur(margins.total_ca_admin_only)}</td>
                      <td colSpan={2}></td>
                      <td className="px-3 py-3.5 text-right font-serif-luxe text-xl text-emerald-300">{fmtEur(margins.total_margin_net)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Grouped list per month */}
          {groupedByMonth.length === 0 ? (
            <div className="card-luxe p-10 text-center text-white/50 italic">
              Aucune recette encaissée en {year} pour le moment.
            </div>
          ) : (
            <div className="card-luxe overflow-hidden">
              <table className="w-full text-sm" data-testid="lr-table">
                <thead className="bg-black/60 text-[10px] uppercase tracking-[0.2em] text-[#DCB457]">
                  <tr>
                    <th className="px-3 py-3 text-center w-12">N°</th>
                    <th className="px-3 py-3 text-left">Date d'encaissement</th>
                    <th className="px-3 py-3 text-left">N° facture</th>
                    <th className="px-3 py-3 text-left">Client</th>
                    <th className="px-3 py-3 text-left">Nature</th>
                    <th className="px-3 py-3 text-center">Mode</th>
                    <th className="px-3 py-3 text-right">Montant TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedByMonth.map(([monthKey, rows]) => {
                    const monthTotal = rows.reduce((s, r) => s + r.amount_ttc, 0);
                    const first = new Date(rows[0].date_encaissement);
                    const monthLabel = first.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
                    return (
                      <React.Fragment key={monthKey}>
                        {rows.map((e) => (
                          <tr key={e.id} className="border-t border-[#DCB457]/8 hover:bg-[#DCB457]/5" data-testid={`lr-row-${e.ordre}`}>
                            <td className="px-3 py-3 text-center text-white/60 font-mono text-[11px]">{e.ordre}</td>
                            <td className="px-3 py-3 text-white/85">{formatDateLong(e.date_encaissement)}</td>
                            <td className="px-3 py-3 font-mono text-[#DCB457] text-[11px]">{e.number || "—"}</td>
                            <td className="px-3 py-3">{e.client_name || "—"}</td>
                            <td className="px-3 py-3 text-white/70">{e.nature}{e.is_deposit && <span className="text-[10px] text-[#DCB457] ml-1">(acompte)</span>}</td>
                            <td className="px-3 py-3 text-center text-white/60 text-[12px]">{e.payment_method}</td>
                            <td className="px-3 py-3 text-right font-medium">{fmtEur(e.amount_ttc)}</td>
                          </tr>
                        ))}
                        <tr className="bg-[#DCB457]/10 border-t border-[#DCB457]/40">
                          <td colSpan={6} className="px-3 py-2.5 text-right text-[#DCB457] text-[11px] uppercase tracking-[0.18em] font-medium capitalize">
                            Total {monthLabel}
                          </td>
                          <td className="px-3 py-2.5 text-right text-[#DCB457] font-bold">{fmtEur(monthTotal)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  <tr className="bg-black border-t-2 border-[#DCB457]">
                    <td colSpan={6} className="px-3 py-4 text-right font-serif-luxe text-lg">TOTAL RECETTES ENCAISSÉES {year}</td>
                    <td className="px-3 py-4 text-right font-serif-luxe text-2xl gold-text">{fmtEur(data.total_ttc)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
